import { ALERT_OPERATOR_LABELS, ALERT_STATUS } from "./alert-types";

const ACK_STORAGE_KEY = "naru_frontend_next_alert_ack";

function toNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function compareValue(actual, threshold, operator) {
  if (!Number.isFinite(actual) || !Number.isFinite(threshold)) {
    return false;
  }
  if (operator === "up") {
    return actual >= threshold;
  }
  if (operator === "down") {
    return actual <= threshold;
  }
  if (operator === "equal") {
    return actual === threshold;
  }
  return false;
}

function flattenFrameMax(frame) {
  if (!Array.isArray(frame) || !frame.length) {
    return null;
  }
  const values = (Array.isArray(frame[0]) ? frame.flat() : frame)
    .map((value) => toNumber(value))
    .filter((value) => value !== null);
  return values.length ? Math.max(...values) : null;
}

function mlxChannelValue(entries, channel) {
  if (!Array.isArray(entries)) {
    return null;
  }
  const match = entries.find((entry) => Number(entry?.channel) === Number(channel));
  if (!match || typeof match !== "object") {
    return null;
  }
  return toNumber(match.object) ?? toNumber(match.ambient);
}

function mlxMax(entries, channels = [2, 3, 4]) {
  const values = channels
    .map((channel) => mlxChannelValue(entries, channel))
    .filter((value) => value !== null);
  return values.length ? Math.max(...values) : null;
}

function pickDirectNumber(payload, keys) {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      const value = toNumber(payload[key]);
      if (value !== null) {
        return value;
      }
    }
  }
  return null;
}

function pickNestedNumber(payload, key) {
  const parts = String(key || "").split(".");
  let current = payload;
  for (const part of parts) {
    if (!current || typeof current !== "object" || !(part in current)) {
      return null;
    }
    current = current[part];
  }
  return toNumber(current);
}

function expandSensorKeys(sensorKey) {
  const raw = String(sensorKey || "").trim();
  if (!raw) {
    return [];
  }
  const match = /\(([^)]+)\)\s*$/.exec(raw);
  const canonical = (match?.[1] || raw).trim();
  if (canonical.toLowerCase() === "frame.object") {
    return ["r", "s", "t", "frame.object"];
  }
  return canonical.split(/[|+/]/).map((part) => part.trim()).filter(Boolean);
}

function resolveSensorValue(payload, name) {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const key = String(name || "").trim();
  const lowered = key.toLowerCase();

  const direct = pickDirectNumber(payload, [key]);
  if (direct !== null) {
    return direct;
  }

  if (lowered === "vibration") {
    return pickDirectNumber(payload, ["vibration"]);
  }
  if (lowered === "object") {
    return pickDirectNumber(payload, ["object"]);
  }
  if (lowered === "frame" || lowered === "frame.object") {
    return flattenFrameMax(payload.frame);
  }
  if (["vib", "vibration.level", "vib_level"].includes(lowered)) {
    return pickDirectNumber(payload, ["vibration", "vib_level"]);
  }
  if (["dev_in_temp", "ambient", "temperature", "temp", "dev-temp", "dev"].includes(lowered)) {
    return (
      pickDirectNumber(payload, ["dev_in_temp", "temp", "temperature", "total_temp", "object", "ambient"]) ??
      mlxMax(payload.mlx90614) ??
      flattenFrameMax(payload.frame)
    );
  }
  if (["r", "r_temp", "r-temp"].includes(lowered)) {
    return pickDirectNumber(payload, ["r_temp", "r", "R"]) ?? mlxChannelValue(payload.mlx90614, 2);
  }
  if (["s", "s_temp", "s-temp"].includes(lowered)) {
    return pickDirectNumber(payload, ["s_temp", "s", "S"]) ?? mlxChannelValue(payload.mlx90614, 3);
  }
  if (["t", "t_temp", "t-temp"].includes(lowered)) {
    return pickDirectNumber(payload, ["t_temp", "t", "T"]) ?? mlxChannelValue(payload.mlx90614, 4);
  }

  return pickNestedNumber(payload, key);
}

function resolveRuleValues(payload, sensorKey) {
  return expandSensorKeys(sensorKey)
    .map((name) => ({ sensor_key: name, value: resolveSensorValue(payload, name) }))
    .filter((entry) => entry.value !== null);
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return Number.isFinite(Number(value)) ? String(Number(value)) : String(value);
}

export function buildAlertSentence(alert = {}) {
  const label = alert.metric_label || alert.metric_key || "센서";
  const actual = formatValue(alert.current_value);
  const threshold = formatValue(alert.threshold_value);
  const operator = ALERT_OPERATOR_LABELS[alert.operator] || "기준";
  return `${label} · 현재 ${actual} / 기준 ${threshold} ${operator}`;
}

export function applyAcknowledgedStatus(alerts = [], acknowledgedMap = {}) {
  return alerts.map((alert) => {
    if (alert.status === ALERT_STATUS.ACKNOWLEDGED) {
      return normalizeAlertEntry(alert);
    }
    const acknowledgedAt = acknowledgedMap[alert.id] || "";
    const detectedAt = alert.detected_at || "";
    const acknowledged =
      acknowledgedAt && detectedAt && new Date(acknowledgedAt) >= new Date(detectedAt);

    return normalizeAlertEntry({
      ...alert,
      status: acknowledged ? ALERT_STATUS.ACKNOWLEDGED : ALERT_STATUS.ALERT,
      acknowledged_at: acknowledgedAt,
    });
  });
}

export function loadAcknowledgedAlerts() {
  try {
    const raw = window.localStorage.getItem(ACK_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_error) {
    return {};
  }
}

export function saveAcknowledgedAlerts(value) {
  window.localStorage.setItem(ACK_STORAGE_KEY, JSON.stringify(value || {}));
}

export function buildAlertId(deviceKey, itemId, metricKey) {
  return `${deviceKey}:${itemId}:${metricKey}`;
}

export function evaluateDeviceAlerts({ deviceKey = "", items = [], latestByItem = {}, acknowledgedMap = {} }) {
  const alerts = [];

  items.forEach((item) => {
    const latestEvent = latestByItem[item.id] || null;
    const payload = latestEvent?.payload;
    if (!payload || typeof payload !== "object") {
      return;
    }

    (Array.isArray(item.sensor_rules) ? item.sensor_rules : []).forEach((rule, index) => {
      const sensorKey = String(rule?.sensor_key || "").trim();
      const operator = String(rule?.comparison_operator || "").trim();
      const threshold = toNumber(rule?.threshold_value);
      if (!sensorKey || !operator || threshold === null) {
        return;
      }

      const values = resolveRuleValues(payload, sensorKey);
      values.forEach((resolved) => {
        if (!compareValue(resolved.value, threshold, operator)) {
          return;
        }

        const metricKey = resolved.sensor_key || sensorKey || `rule-${index + 1}`;
        const alertId = buildAlertId(deviceKey, item.id, metricKey);
        const detectedAt = latestEvent?.received_at || "";
        const acknowledgedAt = acknowledgedMap[alertId] || "";
        const severity =
          acknowledgedAt && detectedAt && new Date(acknowledgedAt) >= new Date(detectedAt)
            ? ALERT_STATUS.ACKNOWLEDGED
            : ALERT_STATUS.ALERT;

        alerts.push(
          normalizeAlertEntry({
            id: alertId,
            device_key: deviceKey,
            device_name: item.name || deviceKey,
            item_id: item.id,
            item_name: item.name || "",
            metric_key: metricKey,
            metric_label: sensorKey,
            current_value: resolved.value,
            threshold_value: threshold,
            operator,
            status: severity,
            detected_at: detectedAt,
            acknowledged_at: acknowledgedAt,
          })
        );
      });
    });
  });

  return alerts.sort((a, b) => String(b.detected_at || "").localeCompare(String(a.detected_at || "")));
}

export function normalizeAlertEntry(entry = {}) {
  return {
    id: entry.id || "",
    device_key: entry.device_key || "",
    device_display_key: entry.device_display_key || entry.device_key || "",
    device_name: entry.device_name || "",
    item_id: entry.item_id || null,
    item_name: entry.item_name || "",
    metric_key: entry.metric_key || "",
    metric_label: entry.metric_label || "",
    current_value: entry.current_value ?? null,
    threshold_value: entry.threshold_value ?? null,
    operator: entry.operator || "",
    detail_url: entry.detail_url || "",
    message: entry.message || "",
    title: entry.title || "",
    status: entry.status || ALERT_STATUS.ALERT,
    detected_at: entry.detected_at || "",
    acknowledged_at: entry.acknowledged_at || "",
  };
}
